/**
 * StaleSyncBanner — calm, single-banner-per-context advisory.
 *
 * Use only at entity / group context level. NEVER spam it on every page.
 * The banner is intentionally quiet:
 *   - amber background, no shadow, no animation
 *   - one calm sentence that names what's stale and the next step
 *   - dismissible (session-only; comes back next page load)
 *
 * Props:
 *   scope          "entity" | "group"
 *   freshnessHours number (how stale)
 *   contextLabel   string (e.g. "All Crane Entity 1" or "All Crane Sales")
 *   nextAction     string (calm one-liner — what to do)
 *   testId         string
 */

import { useState } from "react";
import { Clock, X } from "lucide-react";

function humaniseHours(h) {
    if (h == null) return "no recorded sync";
    if (h < 1) return `${Math.round(h * 60)} min ago`;
    if (h < 48) return `${h.toFixed(1)}h ago`;
    return `${(h / 24).toFixed(1)}d ago`;
}

export function StaleSyncBanner({
    scope = "entity",
    freshnessHours,
    contextLabel,
    nextAction,
    testId = "stale-sync-banner",
}) {
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) return null;

    const lastSync = humaniseHours(freshnessHours);
    const scopeWord = scope === "group" ? "Group" : "Entity";

    return (
        <div
            className="flex items-start gap-3 px-4 py-3 mb-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900"
            data-testid={testId}
            role="status"
        >
            <Clock size={15} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1 text-[13px] leading-relaxed">
                <span className="font-medium">{scopeWord} data is stale.</span>{" "}
                {contextLabel ? <span>{contextLabel} </span> : null}
                last synced {lastSync}.{" "}
                {nextAction ? (
                    <span className="opacity-90">{nextAction}</span>
                ) : null}
            </div>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                className="p-1 -m-1 text-amber-700 hover:text-amber-900 transition-colors flex-shrink-0"
                aria-label="Dismiss"
                data-testid={`${testId}-dismiss`}
            >
                <X size={14} strokeWidth={2} />
            </button>
        </div>
    );
}
